import { useMemo, useState } from 'react';

import {
  Button,
  ContainerColumnLeft,
  SearchForm,
  TableContainer,
  TableHeader,
  Typography,
} from '@occc-app/design-system';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { pluralize } from '@occc-app/lib/strings';
import { RetailerNetworkLocationFilter } from '@occc/graphql';

import RetailerNetworkLocationsTable from './retailer-network-locations-table.component';

import { useGetRetailerNetworkLocations } from '../../../../data-access/queries';

type RetailerNetworkLocationsTableContainerProps = {
  handleBulkAction: (newLocationUuids: string[]) => Promise<void>;
  filter: Omit<RetailerNetworkLocationFilter, 'search'>;
  title: string;
  retailerNetworkLocationGroupUuid: string;
};

export const RetailerNetworkLocationsTableContainer = ({
  handleBulkAction,
  filter,
  title,
  retailerNetworkLocationGroupUuid,
}: RetailerNetworkLocationsTableContainerProps) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { loading, error, data, paginationProps } = useGetRetailerNetworkLocations({
    filter: {
      ...filter,
      search,
      retailerNetworkLocationGroupUuidNotIn: [retailerNetworkLocationGroupUuid],
    },
  });

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const onSubmit = async () => {
    const newLocationUuids = Array.from(bulkSelectedItems);
    await handleBulkAction(newLocationUuids);
  };

  const headerActions: React.ReactNode = useMemo(() => {
    return (
      <SearchForm
        onSubmit={({ search }) => {
          setSearch(search);
        }}
      />
    );
  }, [setSearch]);

  const bulkSelectedItemsCount = bulkSelectedItems.size;

  return (
    <ContainerColumnLeft>
      <TableContainer>
        <TableHeader
          disableTitleTypography
          actions={headerActions}
          title={
            <Typography variant='h6' whiteSpace='nowrap'>
              {title}
            </Typography>
          }
          bulkSelected={bulkSelectedItemsCount}
          bulkActions={
            <Button color='primary' variant='contained' onClick={onSubmit}>
              {`Add selected ${pluralize(bulkSelectedItemsCount, 'Location')}`}
            </Button>
          }
        />
        <RetailerNetworkLocationsTable
          loading={loading}
          data={data}
          error={error}
          paginationProps={paginationProps}
          bulkSelectedItems={bulkSelectedItems}
          handleBulkSelectMultiple={handleBulkSelectMultiple}
          handleBulkDeselectAll={handleBulkDeselectAll}
          handleItemSelect={handleItemSelect}
        />
      </TableContainer>
    </ContainerColumnLeft>
  );
};
